import { makeAutoObservable } from 'mobx'
import {
  CallbackProperty,
  Cartesian3,
  Cartographic,
  Cesium3DTileFeature,
  Cesium3DTileset,
  Math as CesiumMath,
  Color,
  CustomDataSource,
  Ellipsoid,
  EllipsoidTerrainProvider,
  Entity,
  HeightReference,
  JulianDate,
  Model,
  PolygonHierarchy,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,

} from '@cesiumgs/cesium-analytics'
import type { Viewer } from '@cesiumgs/cesium-analytics'
import dayjs from 'dayjs'
import { reqShadowAnalysis, uploadImgToServer } from '@/service/shadeAnalysis'
import { queryaddress } from '@/service/rightNavCtrl'

export class ShadeAnalysis {
  cesiumview: Viewer | null = null
  _handlers3: ScreenSpaceEventHandler | null = null
  _drawLayer: CustomDataSource | null = null
  destPositions: Cartesian3[] = []
  tempDest: Cartesian3[] = []
  ispickclear: boolean = false
  posLTRB: Cartesian3[] = []
  resultPngPath: string = ''
  shdVals: Array<any> = []
  isLoading: boolean = false
  visibleResult: boolean = false
  address: string = ''
  dateData: string = ''
  startTime: string = ''
  endTime: string = ''
  tmSts: string = ''
  percent: number = 0
  constructor() {
    makeAutoObservable(this)
  }

  initfn(view: Viewer | null) {
    this.cesiumview = view
    this._drawLayer = new CustomDataSource('shaderAnalysis')
    this.cesiumview && this.cesiumview.dataSources.add(this._drawLayer)
  }

  async startAnalysis(targetDate: string, startTime: string, endTime: string, wc: string, hc: string, callback: any) {
    this.dateData = targetDate
    this.startTime = startTime
    this.endTime = endTime

    if (this.cesiumview) {
      const tmCur = new Date()
      const tmStart = new Date(`${targetDate} ${startTime}`)
      const tmEnd = new Date(`${targetDate} ${endTime}`)
      const unitMin = 15 // capture once 15 minutes
      const shDir = `ScrnCaptImgs/${this.getLongDateTimeString(tmCur)}`
      const shPath = `${shDir}/${this.getShortDateString(tmCur)}`

      this.cesiumview.scene.globe.enableLighting = true
      this.cesiumview.shadows = true
      this.isLoading = true
      let sss: number = 0
      const prestep = Number(
        (
          ((unitMin * 60 * 1000) / (tmEnd.getTime() - tmStart.getTime()))
        * 100
        ).toFixed(2),
      )
      for (
        let tmSts = tmStart;
      // eslint-disable-next-line no-unmodified-loop-condition
        tmSts <= tmEnd;
        tmSts.setMinutes(tmSts.getMinutes() + unitMin)
      ) {
        const Iso8601 = tmSts.toISOString()

        this.cesiumview.clock.currentTime = JulianDate.fromIso8601(Iso8601)
        const fileNm = `${this.getLongDateTimeString(tmCur)}_${this.getLongDateTimeString(
        tmSts,
      )}.png`
      // let filePath = `${getLongDateTimeString(tmCur)}`;

        await this.sleep(100)
        const blob: Blob = this.cesiumview.getBlob()
        const files = new window.File([blob], `${fileNm}`)
        this.tmSts = dayjs(tmSts).format('hh:mm:ss')
        this.percent = sss
        // yield put({
        //   type: 'save',
        //   payload: {
        //     tmSts: moment(tmSts).format('hh:mm:ss'),
        //     percent: sss,
        //   },
        // })
        sss += prestep
        const formData = new FormData()
        formData.append('file', files)
        formData.append('userAccount', shDir)

        const resServer: any = await uploadImgToServer(formData)
        const res: any = resServer.data
        if (res.code === 200) {
          //
        }
      }
      const destPos = this.getDestPositions()

      const myHeaders = new Headers()
      myHeaders.append('Content-Type', 'application/x-www-form-urlencoded')

      const urlencoded = new URLSearchParams()
      urlencoded.append('path', `${shPath}`)
      urlencoded.append('x1', `${destPos[0].x * window.devicePixelRatio}`)
      urlencoded.append('y1', `${destPos[0].y * window.devicePixelRatio}`)
      urlencoded.append('x2', `${destPos[1].x * window.devicePixelRatio}`)
      urlencoded.append('y2', `${destPos[1].y * window.devicePixelRatio}`)
      urlencoded.append('x3', `${destPos[2].x * window.devicePixelRatio}`)
      urlencoded.append('y3', `${destPos[2].y * window.devicePixelRatio}`)
      urlencoded.append('wc', wc)
      urlencoded.append('hc', hc)

      const params = [
        {
          name: 'path',
          value: shPath,
        },
        {
          name: 'x1',
          value: destPos[0].x * window.devicePixelRatio,
        },
        {
          name: 'y1',
          value: destPos[0].y * window.devicePixelRatio,
        },
        {
          name: 'x2',
          value: destPos[1].x * window.devicePixelRatio,
        },
        {
          name: 'y2',
          value: destPos[1].y * window.devicePixelRatio,
        },
        {
          name: 'x3',
          value: destPos[2].x * window.devicePixelRatio,
        },
        {
          name: 'y3',
          value: destPos[2].y * window.devicePixelRatio,
        },
        {
          name: 'wc',
          value: wc,
        },
        {
          name: 'hc',
          value: hc,
        },
      ]

      const resAnalysis: any = await reqShadowAnalysis(params)
      const response = resAnalysis.data
      if (response && response.code === 200) {
        callback(false)
        const arr = response.data.result.split('path')
        if (arr.length > 1) {
          const p = arr[1].substring(1)
          this.resultPngPath = `${window.SystemConfig.lxdt_shareUrl}/files/${p}/result.png`
          try {
            const lines = arr[0].split(/\r?\n/)
            const lineCnt = lines.length
            const lastLine = lines[lineCnt - 1]
              .replace('{', '')
              .replace('}', '')
              .replace('total', '')
            const totCnt = Number(lines[lineCnt - 2].split('{')[0])
            const unitVals = lastLine.replace(/(\s*)/g, '').split(',')
            unitVals.length = unitVals.length - 1 // remove last item

            const shdVals1 = []
            for (let ind = 0; ind < unitVals.length; ind++) {
              const val = (Number(unitVals[ind]) / totCnt) * 100
              shdVals1.push(val)
            }

            const ltrbpos = this.getLeftTopPos()
            const rage = {
              rage1: 0,
              rage2: 0,
              rage3: 0,
              rage4: 0,
              rage5: 0,
              rage6: 0,
              rage7: 0,
              rage8: 0,
              rage9: 0,
            }
            shdVals1.forEach((item) => {
              if (item >= 0 && item < 20) {
                rage.rage1 += 1
              }
              else if (item >= 20 && item < 30) {
                rage.rage2 += 1
              }
              else if (item >= 30 && item < 40) {
                rage.rage3 += 1
              }
              else if (item >= 40 && item < 50) {
                rage.rage4 += 1
              }
              else if (item >= 50 && item < 60) {
                rage.rage5 += 1
              }
              else if (item >= 60 && item < 70) {
                rage.rage6 += 1
              }
              else if (item >= 70 && item < 80) {
                rage.rage7 += 1
              }
              else if (item >= 80 && item < 90) {
                rage.rage8 += 1
              }
              else if (item >= 90) {
                rage.rage9 += 1
              }
            })
            const newshdVals = [
              {
                key: 1,
                range: '10%이상 20%미만',
                color: '#AA3B28',
                rate: `${((rage.rage1 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 2,
                range: '20%이상 30%미만',
                color: '#C54E34',
                rate: `${((rage.rage2 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 3,
                range: '30%이상 40%미만',
                color: '#C57057',
                rate: `${((rage.rage3 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 4,
                range: '40%이상 50%미만',
                color: '#D1A794',
                rate: `${((rage.rage4 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 5,
                range: '50%이상 60%미만',
                color: '#E2E2E2',
                rate: `${((rage.rage5 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 6,
                range: '60%이상 70%미만',
                color: '#B4D3EA',
                rate: `${((rage.rage6 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 7,
                range: '70%이상 80%미만',
                color: '#81B8E6',
                rate: `${((rage.rage7 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 8,
                range: '80%이상 90%미만',
                color: '#6BA8EC',
                rate: `${((rage.rage8 / shdVals1.length) * 100).toFixed(2)}%`,
              },
              {
                key: 9,
                range: '90%이상',
                color: '#5385C3',
                rate: `${((rage.rage9 / shdVals1.length) * 100).toFixed(2)}%`,
              },
            ]
            this.shdVals = newshdVals

            this.drawGridSurface(Number(wc), Number(hc), shdVals1)
            this.isLoading = false
            this.cesiumview.scene.globe.enableLighting = false
            this.cesiumview.shadows = false
            this.visibleResult = true
          }
          catch (err) {
          }
        }
      }
      else {
        callback(false)
      }
    }
  }

  sleep(delay: number) {
    return new Promise(resolve => setTimeout(resolve, delay))
  }

  getLeftTopPos(): Cartesian3[] {
    const retPos = []
    if (this.posLTRB.length > 1) {
      retPos.push(this.posLTRB[0])
      retPos.push(this.posLTRB[1])
    }
    return retPos
  }

  drawGridSurface(widthCol: number, heightCol: number, shadLevel: Array<any>) {
    const dstefflng = 0.0000008 // coefficient  (30.8m equivalent of 1 second) (longitude)
    const dstefflat = 0.0000008 // coefficient  (25m equivalent of 1 second) (latitude)
    // let camPos = this.transformCartesianToWGS84(this.cesiumview.scene.camera.position);
    let camPos: { lng: number; lat: number; alt: number }
    if (this.cesiumview) {
      camPos = this.cartesianToDegrees(this.cesiumview.scene.camera.position)

      const ltrbPos = this.getLeftTopPos()

      // LEFT TOP POINT
      const temp1 = this.cartesianToDegrees(ltrbPos[0])
      // RIGHT BOTTOM POINT
      const temp3 = this.cartesianToDegrees(ltrbPos[1])

      // LOCATION MODIFIED - NOT TO BE DISPLAYED INSIDE THE BUILDING
      const temp_pos1 = {
        lng:
        camPos.lng >= temp1.lng ? temp1.lng + dstefflng : temp1.lng - dstefflng,
        lat:
        camPos.lat >= temp1.lat ? temp1.lat + dstefflat : temp1.lat - dstefflat,
        alt: temp1.alt,
      }

      // LOCATION MODIFIED - NOT TO BE DISPLAYED INSIDE THE BUILDING
      const temp_pos3 = {
        lng:
        camPos.lng >= temp3.lng ? temp3.lng + dstefflng : temp3.lng - dstefflng,
        lat:
        camPos.lat >= temp3.lat ? temp3.lat + dstefflat : temp3.lat - dstefflat,
        alt: temp3.alt,
      }

      const widCol = widthCol
      const hgtCol = heightCol

      const unitCords = this.getUnitsHorizonal(temp_pos1, temp_pos3, widCol)

      const unitHgts = this.getUnitsVertical(temp_pos1, temp_pos3, hgtCol)

      const gridCord = []
      for (let ind: number = 0; ind < widCol; ind++) {
        for (let indh: number = 0; indh < hgtCol; indh++) {
          const pgHier = new PolygonHierarchy()
          // LEFT TOP
          pgHier.positions.push(
            Cartesian3.fromDegrees(
              unitCords[ind].lng,
              unitCords[ind].lat,
              unitHgts[indh].alt,
            ),
          )
          pgHier.positions.push(
            Cartesian3.fromDegrees(
              unitCords[ind].lng,
              unitCords[ind].lat,
              unitHgts[indh + 1].alt,
            ),
          )
          // RIGHT BOTTOM
          pgHier.positions.push(
            Cartesian3.fromDegrees(
              unitCords[ind + 1].lng,
              unitCords[ind + 1].lat,
              unitHgts[indh + 1].alt,
            ),
          )
          pgHier.positions.push(
            Cartesian3.fromDegrees(
              unitCords[ind + 1].lng,
              unitCords[ind + 1].lat,
              unitHgts[indh].alt,
            ),
          )

          gridCord.push(pgHier)
        }
      }

      const gridCnt = gridCord.length
      for (let ind = 0; ind < gridCnt; ind++) {
        const gridColor = this.getGridColorwithValue(shadLevel[ind])
        this.cesiumview.entities.add({
          name: String(ind),
          polygon: {
            hierarchy: gridCord[ind],
            perPositionHeight: true,
            heightReference: HeightReference.RELATIVE_TO_GROUND,
            material: gridColor,
            outline: true,
            outlineWidth: 4,
            outlineColor: Color.BLACK,
          },
        })
      }
    }
  }

  clearSimple() {
    // if (this._drawLayer) {
    //   this._drawLayer.entities.removeAll()
    // }
    if (this.cesiumview) {
      this.cesiumview.entities.removeAll()
    }
  }

  distory() {
    if (this._drawLayer) {
      this._drawLayer.entities.removeAll()
    }
    if (this.cesiumview) {
      this.cesiumview.entities.removeAll()
    }
    if (this._handlers3) {
      this._handlers3.destroy()
      this._handlers3 = null
    }

    if (this.destPositions) {
      this.destPositions = []
    }
    this.destPositions = []
    this.tempDest = []
    this.posLTRB = []
    this.shdVals = []
    this.visibleResult = false
  }

  getGridColorwithValue(shadowLevel: number) {
    const alpha = 128
    let retColor = Color.fromBytes(255, 255, 255, alpha)
    if (shadowLevel >= 0 && shadowLevel <= 20) {
      retColor = Color.fromBytes(170, 59, 40, alpha)
    }
    else if (shadowLevel > 20 && shadowLevel <= 30) {
      retColor = Color.fromBytes(197, 78, 52, alpha)
    }
    else if (shadowLevel > 30 && shadowLevel <= 40) {
      retColor = Color.fromBytes(197, 112, 87, alpha)
    }
    else if (shadowLevel > 40 && shadowLevel <= 50) {
      retColor = Color.fromBytes(209, 167, 148, alpha)
    }
    else if (shadowLevel > 50 && shadowLevel <= 60) {
      retColor = Color.fromBytes(226, 226, 226, alpha)
    }
    else if (shadowLevel > 60 && shadowLevel <= 70) {
      retColor = Color.fromBytes(180, 211, 234, alpha)
    }
    else if (shadowLevel > 70 && shadowLevel <= 80) {
      retColor = Color.fromBytes(132, 189, 236, alpha)
    }
    else if (shadowLevel > 80 && shadowLevel <= 90) {
      retColor = Color.fromBytes(107, 168, 236, alpha)
    }
    else if (shadowLevel > 90) {
      retColor = Color.fromBytes(83, 133, 195, alpha)
    }

    return retColor
  }

  getUnitsHorizonal(pos1: any, pos2: any, unitNum: any) {
    const xUnit = (pos2.lng - pos1.lng) / unitNum
    const yUnit = (pos1.lat - pos2.lat) / unitNum

    const retCoord = []
    for (let ind = 0; ind <= unitNum; ind++) {
      retCoord.push({
        lng: pos1.lng + ind * xUnit,
        lat: pos1.lat - ind * yUnit,
      })
    }

    return retCoord
  }

  getUnitsVertical(pos1: any, pos2: any, unitNum: any) {
    const hgtUnit = (pos1.alt - pos2.alt) / unitNum

    const retHgt = []
    for (let ind = 0; ind <= unitNum; ind++) {
      retHgt.push({
        alt: pos1.alt - ind * hgtUnit,
      })
    }

    return retHgt
  }

  getDestPositions() {
    const retPos: Array<any> = []
    if (this.destPositions.length >= 3) {
      this.destPositions.forEach((pos) => {
        retPos.push(pos)
      })
    }
    return retPos
  }

  getLongDateTimeString(date: Date) {
    const year = date.getFullYear()
    const month = this.leftPad(date.getMonth() + 1)
    const day = this.leftPad(date.getDate())
    const hour = this.leftPad(date.getHours())
    const minute = this.leftPad(date.getMinutes())
    const second = this.leftPad(date.getSeconds())

    return `${year}${month}${day}${hour}${minute}${second}`
  }

  getShortDateString(date: Date) {
    const year = date.getFullYear()
    const month = this.leftPad(date.getMonth() + 1)
    const day = this.leftPad(date.getDate())

    return `${year}${month}${day}`
  }

  leftPad(value: number) {
    if (value >= 10) {
      return value
    }

    return `0${value}`
  }

  drawLineMeasureSpace(callback: any) {
    if (this.cesiumview && callback) {
      let positions: Cartesian3[] = []
      let _lineEntity = null

      this._handlers3 = new ScreenSpaceEventHandler(
        this.cesiumview.scene.canvas,
      )
      if (this.cesiumview.entities.getById('gridoutline')) {
        this.cesiumview.entities.remove(
          this.cesiumview.entities.getById('gridoutline') as Entity,
        )
      }

      const destCnt = 3
      this.destPositions = []
      this.tempDest = []

      let layername = ''
      this._handlers3.setInputAction((movement: any) => {
        const cartesian = this.getCatesian3FromPX(movement.position)

        if (this.ispickclear) {
          return false
        }
        if (cartesian && cartesian.x) {
          if (positions.length === 0) {
            positions.push(cartesian.clone())
            layername = `m${new Date().getTime()}`
            this._handlers3!.setInputAction((movement: any) => {
              const cartesian = this.getCatesian3FromPX(movement.endPosition)
              if (positions.length >= 2) {
                if (cartesian && cartesian.x) {
                  positions.pop()
                  positions.push(cartesian)
                }
              }
            }, ScreenSpaceEventType.MOUSE_MOVE)
          }
          //   // add measure information point
          //   this.addInfoPoint(cartesian, layername, positions, 1);
          positions.push(cartesian)

          this.tempDest.push(movement.position.clone())

          // // add up screen coordiates
          // tempDest.push(movement.position);
        }
      }, ScreenSpaceEventType.LEFT_CLICK)

      // right
      this._handlers3.setInputAction(() => {
        if (this.tempDest.length >= destCnt) {
          // this._handlers3.removeInputAction(
          //   Cesium.ScreenSpaceEventType.MOUSE_MOVE,
          // );
          // this._handlers3.removeInputAction(
          //   Cesium.ScreenSpaceEventType.LEFT_CLICK,
          // );
          this._handlers3!.destroy()
          this._handlers3 = null

          if (typeof callback === 'function') {
            callback(
              this.transformCartesianArrayToWGS84Array(positions),
            )
          }

          this.destPositions = []
          for (let ind = 0; ind < destCnt; ind++) {
            this.destPositions.push(this.tempDest[ind])
          }

          // if(positions.length >= 3){
          //   this.drawGridSurface(positions[0], positions[2], 8, 7);
          // }

          this.posLTRB = []
          this.posLTRB.push(positions[0])
          this.posLTRB.push(positions[2])

          if (positions.length >= 3) {
            this.drawGridOutline()
            const cartographic = Ellipsoid.WGS84.cartesianToCartographic(positions[0])
            const lat = CesiumMath.toDegrees(cartographic.latitude)
            const lng = CesiumMath.toDegrees(cartographic.longitude)
            queryaddress({ lat, lng }).then((res: any) => {
              if (res.returnCode === '200' && res.contents && res.contents.result && res.contents.result.jusoList && res.contents.result.jusoList.jusoList) {
                this.address = res.contents.result.jusoList.jusoList.jibunAddr
              }
            })
          }

          positions = []
        }
        else {
          alert('음영분석 대상 영역 포인트를 3곳 이상 지정해야 합니다.')
        }
      }, ScreenSpaceEventType.RIGHT_CLICK)

      _lineEntity = new Entity({
        name: layername,
        polyline: {
          positions: new CallbackProperty(() => {
            return positions
          }, false),
          width: 4,
          material: Color.RED.withAlpha(0.7),
          // clampToGround: true,
        },
      })

      this._drawLayer!.entities.add(_lineEntity)
    }
  }

  undrawLineMeasureSpace() {
    if (this._handlers3) {
      this._handlers3.destroy()
      this._handlers3 = null
    }
    if (this.cesiumview!.entities.getById('gridoutline')) {
      this.cesiumview!.entities.remove(
        this.cesiumview!.entities.getById('gridoutline') as Entity,
      )
    }
  }

  drawGridOutline() {
    const dstefflng = 0.0000008 // coefficient  (30.8m equivalent of 1 second) (longitude)
    const dstefflat = 0.0000008 // coefficient  (25m equivalent of 1 second) (latitude)
    // let camPos = this.transformCartesianToWGS84(this.cesiumview.scene.camera.position);
    const camPos = this.cartesianToDegrees(this.cesiumview!.scene.camera.position)

    const ltrbPos = this.getLeftTopPos()

    // LEFT TOP POINT
    const temp1 = this.cartesianToDegrees(ltrbPos[0])
    // RIGHT BOTTOM POINT
    const temp3 = this.cartesianToDegrees(ltrbPos[1])

    // LOCATION MODIFIED - NOT TO BE DISPLAYED INSIDE THE BUILDING
    const temp_pos1 = {
      lng:
            camPos.lng >= temp1.lng ? temp1.lng + dstefflng : temp1.lng - dstefflng,
      lat:
            camPos.lat >= temp1.lat ? temp1.lat + dstefflat : temp1.lat - dstefflat,
      alt: temp1.alt,
    }

    // LOCATION MODIFIED - NOT TO BE DISPLAYED INSIDE THE BUILDING
    const temp_pos3 = {
      lng:
            camPos.lng >= temp3.lng ? temp3.lng + dstefflng : temp3.lng - dstefflng,
      lat:
            camPos.lat >= temp3.lat ? temp3.lat + dstefflat : temp3.lat - dstefflat,
      alt: temp3.alt,
    }

    const pgHier = new PolygonHierarchy()
    // LEFT TOP
    pgHier.positions.push(
      Cartesian3.fromDegrees(
        temp_pos1.lng,
        temp_pos1.lat,
        temp_pos1.alt,
      ),
    )
    pgHier.positions.push(
      Cartesian3.fromDegrees(
        temp_pos1.lng,
        temp_pos1.lat,
        temp_pos3.alt,
      ),
    )
    // RIGHT BOTTOM
    pgHier.positions.push(
      Cartesian3.fromDegrees(
        temp_pos3.lng,
        temp_pos3.lat,
        temp_pos3.alt,
      ),
    )
    pgHier.positions.push(
      Cartesian3.fromDegrees(
        temp_pos3.lng,
        temp_pos3.lat,
        temp_pos1.alt,
      ),
    )
    // const orientation = {
    //   heading: CesiumMath.toRadians(0),
    //   pitch: CesiumMath.toRadians(0),
    //   roll: CesiumMath.toRadians(-180),
    // }
    this.cesiumview!.entities.add({
      id: 'gridoutline',
      name: 'gridoutline',
      polygon: {
        hierarchy: pgHier,
        perPositionHeight: true,
        material: Color.GRAY.withAlpha(0.5),
        outline: true,
        outlineWidth: 4,
        outlineColor: Color.RED,
      },
    })
    // plg.orientation = orientation
  }

  cartesianToDegrees(cart: Cartesian3) {
    // let pos = Cesium.Cartographic.fromCartesian(cart);
    const pos = Ellipsoid.WGS84.cartesianToCartographic(cart)
    return {
      lng: CesiumMath.toDegrees(pos.longitude),
      lat: CesiumMath.toDegrees(pos.latitude),
      // lng: pos.longitude/Math.PI * 180,
      // lat: pos.latitude/Math.PI * 180,
      alt: pos.height,
    }
  }

  getCatesian3FromPX(px: Cartesian3) {
    if (this.cesiumview && px) {
      const picks = this.cesiumview.scene.drillPick(px)
      let cartesian: any = null
      let isOn3dtiles = false
      let isOnTerrain = false
      // drillPick
      for (const i in picks) {
        const pick = picks[i]

        if (
          (pick && pick.primitive instanceof Cesium3DTileFeature)
          || (pick && pick.primitive instanceof Cesium3DTileset)
          || (pick && pick.primitive instanceof Model)
        ) {
          // Pick on model
          isOn3dtiles = true
        }
        // 3dtilset
        if (isOn3dtiles) {
          this.cesiumview.scene.pick(px) // pick
          cartesian = this.cesiumview.scene.pickPosition(px)
          if (cartesian) {
            const cartographic = Cartographic.fromCartesian(cartesian)
            if (cartographic.height < 0)
              cartographic.height = 0
            const lon = CesiumMath.toDegrees(cartographic.longitude)
            const lat = CesiumMath.toDegrees(cartographic.latitude)
            const height = cartographic.height
            cartesian = this.transformWGS84ToCartesian({
              lng: lon,
              lat,
              alt: height,
            })
          }
        }
      }
      // terrain
      const boolTerrain
        = this.cesiumview.terrainProvider
        instanceof EllipsoidTerrainProvider
      // Terrain
      if (!isOn3dtiles && !boolTerrain) {
        const ray = this.cesiumview.scene.camera.getPickRay(px)
        if (!ray)
          return null
        cartesian = this.cesiumview.scene.globe.pick(
          ray,
          this.cesiumview.scene,
        )
        isOnTerrain = true
      }
      // global
      if (!isOn3dtiles && !isOnTerrain && boolTerrain) {
        cartesian = this.cesiumview.scene.camera.pickEllipsoid(
          px,
          this.cesiumview.scene.globe.ellipsoid,
        )
      }
      if (cartesian) {
        const position: {
          lng: number
          lat: number
          alt: number
        } | null = this.transformCartesianToWGS84(cartesian)
        if (position && position.alt < 0) {
          cartesian = this.transformWGS84ToCartesian(position, 0.1)
        }
        return cartesian
      }
      return false
    }
  }

  transformWGS84ToCartesian(position: any, alt?: any) {
    if (this.cesiumview) {
      return position
        ? Cartesian3.fromDegrees(
          position.lng || position.lon,
          position.lat,
          (position.alt = alt || position.alt),
          Ellipsoid.WGS84,
        )
        : Cartesian3.ZERO
    }
  }

  transformCartesianToWGS84(cartesian: any) {
    if (this.cesiumview && cartesian) {
      const ellipsoid = Ellipsoid.WGS84
      const cartographic = ellipsoid.cartesianToCartographic(cartesian)
      return {
        lng: CesiumMath.toDegrees(cartographic.longitude),
        lat: CesiumMath.toDegrees(cartographic.latitude),
        alt: cartographic.height ? cartographic.height : 0,
      }
    }
    else {
      return null
    }
  }

  transformCartesianArrayToWGS84Array(cartesianArr: any) {
    if (this.cesiumview) {
      return cartesianArr
        ? cartesianArr.map((item: any) => {
          return this.transformCartesianToWGS84(item)
        })
        : []
    }
  }
}
